import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCompanyDashboard,
  createApprovalRule,
  updateApprovalFlow,
  deleteApprovalFlow,
  deleteApprovalStep,
  getInitializeServicesData,
} from "../../../redux/slices/companyDashboardSlice";
import { getCategories, selectPersonalDashboard } from "../../../redux/slices/personalDashboardSlice";
import Checkbox from "../../Checkbox/Checkbox";
import Button from "../../Buttons/button";
import TextField from "../../TextField/TextField";
import errorIcon from "../../../assets/icons/error-icon.svg";
import { Listbox, Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { Fragment } from "react";
import ConfirmModel from "../../models/confirm-model/confirm-model"
import SearchSelectDropdown from '../../Dropdown/search-select-dropdown'

export default function ApprovalFlowModel({
  popup,
  state,
  setstate,
  setPopup,
  setNewApprovalFlowAdded,
  selectedApprovalFlow,
  setApprovalFlowUpdated,
}) {
  const dispatch = useDispatch();
  const { CategoriesAndUsersData } = useSelector(selectCompanyDashboard);
  const State = useSelector(selectPersonalDashboard);
  const { categoriesData } = State;
  const [flowName, setFlowName] = useState("");
  const [category, setCategory] = useState(null);
  const [approvers, setApprovers] = useState([
    { step: "", approver: "", note: "", minSpend: "" },
  ]);

  const [formValidFlowName, setFormValidFlowName] = useState(true);
  const [formValidCategory, setFormValidCategory] = useState(true);
  const [formValidApprovers, setFormValidApprovers] = useState(true);
  const [validationMessage, setValidationMessage] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [removeStepId, setRemoveStepId] = useState(null);
  const [confirmPopup, setConfirmPopup] = useState(false);
  const [clickedButton, setClickedButton] = useState(null);
  const [commentVisible, setCommentVisible] = useState(false);

  useEffect(() => {
    dispatch(getInitializeServicesData());
    dispatch(getCategories());
  }, [dispatch]);

  useEffect(() => {
    if (selectedApprovalFlow?.id) {
      setFlowName(selectedApprovalFlow.name);
      setCategory(selectedApprovalFlow.category?.id || null);
      setCommentVisible(selectedApprovalFlow.comment_visible || false);
      setApprovers(
        selectedApprovalFlow.approval_steps
          ?.filter(step => !step.archived)
          .map(step => ({
            id: step.id || "",
            step: step.step_number?.toString() || "",
            approver: step.approver?.id || "",
            note: step.note_for_approver || "",
            minSpend: step.min_spend?.toString() || "",
          })) || [{ step: "", approver: "", note: "", minSpend: "" }]
      );
    }
  }, [selectedApprovalFlow]);

  useEffect(() => {
    if (formSubmitted) {
      if (selectedApprovalFlow?.id) {
        handleUpdateApprovalFlow();
      } else {
        handleAddApprovalFlow();
      }
    }
  }, [formSubmitted, selectedApprovalFlow]);

  const removeApprover = (index) => {
    const approver = approvers[index];
    if (approver.id) {
      setClickedButton("deleteStep");
      setRemoveStepId(approver.id);
      setConfirmPopup(true);
    } else {
      setApprovers((prev) => prev.filter((_, i) => i !== index));
      // Revalidate form after removing an approver
      if (formSubmitted) {
        checkForm();
      }
    }
  };

  useEffect(() => {
    if (confirmDelete) {
      if (clickedButton === "deleteStep" && removeStepId) {
        dispatch(deleteApprovalStep(removeStepId));
        setApprovers((prev) => prev.filter((a) => a.id !== removeStepId));
        if (formSubmitted) {
          checkForm();
        }
      } else if (clickedButton === "deleteFlow" && selectedApprovalFlow?.id) {
        dispatch(deleteApprovalFlow(selectedApprovalFlow?.id));
      }
      setConfirmDelete(false);
      setConfirmPopup(false);
      setRemoveStepId(null);
      setClickedButton(null);
    }
  }, [confirmDelete]);

  const checkForm = () => {
    let isValid = true;

    if (!flowName.trim()) {
      setFormValidFlowName(false);
      isValid = false;
    } else {
      setFormValidFlowName(true);
    }

    if (!category) {
      setFormValidCategory(false);
      isValid = false;
    } else {
      setFormValidCategory(true);
    }

    if (
      !approvers.length ||
      approvers.some(
        (a) =>
          !a.approver ||
          !a.step.trim() ||
          // !a.note.trim() ||
          !a.minSpend.trim()
      )
    ) {
      setFormValidApprovers(false);
      isValid = false;
    } else {
      setFormValidApprovers(true);
    }

    setValidationMessage(isValid ? "" : "Incomplete Fields");
    return isValid;
  };

  const submitApprovalFlowForm = () => {
    if (!formSubmitted) {
      setFormSubmitted(true);
      checkForm(); // Trigger validation on submit
    }
  };

  const addApprover = () => {
    setApprovers([
      ...approvers,
      { step: "", approver: "", note: "", minSpend: "" },
    ]);
  };

  const updateApprover = (index, field, value) => {
    const newApprovers = [...approvers];
    newApprovers[index][field] = value;
    setApprovers(newApprovers);
    // Revalidate form dynamically when typing in Approver fields
    if (formSubmitted) {
      checkForm();
    }
  };

  const handleAddApprovalFlow = () => {
    if (checkForm()) {
      const payload = {
        approval_rule: {
          name: flowName,
          category_id: category,
          approvers: approvers.map(a => ({
            step: a.step,
            user_id: a.approver,
            note_for_approver: a.note,
            min_spend: a.minSpend
          }))
        }
      };
      dispatch(createApprovalRule(payload));
      setPopup(false);
    }
  };

  const handleUpdateApprovalFlow = () => {
    if (checkForm()) {
      const payload = {
        approval_rule: {
          name: flowName,
          category_id: category,
          comment_visible: commentVisible, 
          approvers: approvers.map(a => ({
            id: a.id,
            step: a.step,
            user_id: a.approver,
            note_for_approver: a.note,
            min_spend: a.minSpend
          }))
        }
      };

      dispatch(updateApprovalFlow(selectedApprovalFlow.id, payload));
      setPopup(false);
    }
  };

  const handleDeleteApprovalFlow = () => {
    if (selectedApprovalFlow?.id) {
      setClickedButton("deleteFlow");
      setConfirmPopup(true);
    }
  };

  return (
    <div className="transform rounded-[12px] md:rounded-[24px] bg-[#363D50] transition-all lg:w-[1020px] lg:h-[700px] flex">
      {/* Left section */}
      <div className="bg-[#242B3E] rounded-l-[24px] w-[320px] h-[700px] p-[32px] hidden lg:flex flex-col items-center justify-center">
        <div className="p-[10px] bg-[#363D50] rounded-[16px]">
          <svg
            width="56"
            height="56"
            viewBox="0 0 56 56"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M34.3103 8.24217C35.4062 7.53505 36.6034 7.13699 37.882 7.13699C38.7055 7.13699 39.3899 7.32908 39.9373 7.63208C40.5341 7.96248 41.2858 7.74649 41.6162 7.14963C41.9466 6.55278 41.7306 5.80109 41.1338 5.47068C40.1994 4.95342 39.0996 4.6665 37.882 4.6665C36.0653 4.6665 34.4096 5.238 32.9708 6.1663C32.3976 6.53617 32.2327 7.30071 32.6026 7.87394C32.9725 8.44718 33.737 8.61204 34.3103 8.24217ZM43.9647 8.99734C43.7695 8.34367 43.0813 7.97206 42.4276 8.16732C41.774 8.36258 41.4024 9.05076 41.5976 9.70443C41.7893 10.346 41.8103 11.0581 41.6269 11.7696C41.4781 12.3465 41.2575 12.8091 40.9916 13.1757C40.6887 13.5933 40.6852 14.1368 40.9403 14.5502H40.6868C40.3622 14.2605 39.8941 14.1514 39.4545 14.3055C39.0063 14.4627 38.4818 14.5502 37.882 14.5502C37.1998 14.5502 36.6468 15.1032 36.6468 15.7854C36.6468 16.4676 37.1998 17.0207 37.882 17.0207C38.0013 17.0207 38.1199 17.0182 38.2378 17.0132C38.2829 17.0182 38.3287 17.0207 38.3751 17.0207H42.822C43.9811 17.0207 44.6935 17.2528 45.1454 17.5442C45.7188 17.9139 46.4832 17.7488 46.853 17.1755C47.2227 16.6022 47.0576 15.8377 46.4843 15.468C45.5678 14.8769 44.4214 14.5809 43.044 14.5525C43.4852 13.9239 43.8103 13.1963 44.0191 12.3863C44.314 11.2424 44.2859 10.0726 43.9647 8.99734ZM30.7367 12.225C31.0725 11.6311 30.8634 10.8775 30.2696 10.5417C29.6758 10.2058 28.9221 10.415 28.5863 11.0088C28.365 11.4001 28.1585 11.801 27.9674 12.209C27.7777 11.8122 27.5747 11.4217 27.3586 11.04C27.0226 10.4464 26.2688 10.2376 25.6752 10.5737C25.0815 10.9098 24.8727 11.6635 25.2088 12.2571C25.6222 12.9874 25.9833 13.76 26.2875 14.5502H23.5522C22.87 14.5502 22.317 15.1033 22.317 15.7855C22.317 16.4677 22.87 17.0207 23.5522 17.0207H27.9751C27.9913 17.021 28.0077 17.021 28.0241 17.0207H32.446C33.1282 17.0207 33.6812 16.4677 33.6812 15.7855C33.6812 15.1033 33.1282 14.5502 32.446 14.5502H29.6681C29.959 13.7501 30.3175 12.9661 30.7367 12.225ZM15.0086 13.1757C15.3116 13.5933 15.3151 14.1368 15.06 14.5502H15.3135C15.638 14.2605 16.1061 14.1514 16.5457 14.3055C16.994 14.4627 17.5184 14.5502 18.1183 14.5502C18.8005 14.5502 19.3535 15.1032 19.3535 15.7854C19.3535 16.4676 18.8005 17.0207 18.1183 17.0207C17.9985 17.0207 17.8793 17.0182 17.7609 17.0131C17.7157 17.0181 17.6697 17.0207 17.6231 17.0207H13.1762C12.0171 17.0207 11.3047 17.2528 10.8528 17.5442C10.2794 17.9139 9.51494 17.7488 9.14523 17.1755C8.77551 16.6022 8.94058 15.8377 9.51391 15.468C10.4309 14.8766 11.5779 14.5806 12.9562 14.5525C12.515 13.9238 12.19 13.1963 11.9812 12.3863C11.6862 11.2424 11.7143 10.0726 12.0355 8.99734C12.2308 8.34367 12.919 7.97206 13.5726 8.16732C14.2263 8.36258 14.5979 9.05076 14.4027 9.70443C14.211 10.346 14.19 11.0581 14.3734 11.7696C14.5222 12.3465 14.7427 12.8091 15.0086 13.1757ZM48.871 19.9336C48.7892 19.2563 48.1738 18.7736 47.4966 18.8554C46.8193 18.9372 46.3365 19.5525 46.4183 20.2298C46.5261 21.1216 46.5277 22.112 46.5277 23.1969C46.5277 24.1692 46.5271 25.0736 46.4662 25.9017C46.4161 26.582 46.9271 27.1742 47.6075 27.2242C48.2879 27.2742 48.88 26.7632 48.93 26.0829C48.9982 25.1558 48.9982 24.1664 48.9982 23.2221V23.1575V23.1574C48.9982 22.1172 48.9982 20.987 48.871 19.9336ZM9.57984 20.2298C9.66164 19.5525 9.17891 18.9372 8.50162 18.8554C7.82434 18.7736 7.20898 19.2563 7.12719 19.9336C6.99996 20.987 6.99998 22.1172 7 23.1574L7 23.222C7 24.1664 6.99999 25.1558 7.06817 26.0829C7.11821 26.7632 7.71032 27.2742 8.39069 27.2242C9.07106 27.1742 9.58204 26.582 9.532 25.9017C9.4711 25.0736 9.47048 24.1692 9.47048 23.1969C9.47048 22.112 9.47214 21.1216 9.57984 20.2298ZM47.5074 30.3091C47.9635 29.8018 47.922 29.0208 47.4146 28.5647C46.9073 28.1086 46.1263 28.1502 45.6702 28.6576C45.4025 28.9553 44.9618 29.2175 44.0572 29.2175C43.375 29.2175 42.822 29.7705 42.822 30.4527V33.464C42.822 34.1462 43.375 34.6992 44.0572 34.6992C44.7394 34.6992 45.2925 34.1462 45.2925 33.464V31.5632C46.1843 31.3703 46.9286 30.9531 47.5074 30.3091ZM10.328 28.6576C9.87193 28.1502 9.09093 28.1086 8.58358 28.5647C8.07624 29.0208 8.03467 29.8018 8.49074 30.3091C9.06963 30.9531 9.81386 31.3703 10.7057 31.5632V33.464C10.7057 34.1462 11.2588 34.6992 11.941 34.6992C12.6232 34.6992 13.1762 34.1462 13.1762 33.464V30.4527C13.1762 29.7705 12.6232 29.2175 11.941 29.2175C11.0364 29.2175 10.5957 28.9553 10.328 28.6576ZM13.1762 37.479C13.1762 36.7968 12.6232 36.2438 11.941 36.2438C11.2588 36.2438 10.7057 36.7968 10.7057 37.479V40.4903C10.7057 41.4979 10.9479 42.4525 11.3784 43.2956C11.6886 43.9032 12.4326 44.1443 13.0402 43.8341C13.6478 43.5239 13.8889 42.7799 13.5787 42.1723C13.3217 41.6689 13.1762 41.0984 13.1762 40.4903V37.479ZM45.2925 37.479C45.2925 36.7968 44.7394 36.2438 44.0572 36.2438C43.375 36.2438 42.822 36.7968 42.822 37.479V40.4903C42.822 41.0984 42.6765 41.6689 42.4195 42.1723C42.1093 42.7799 42.3503 43.5239 42.9579 43.8341C43.5655 44.1443 44.3096 43.9032 44.6198 43.2956C45.0502 42.4525 45.2925 41.4979 45.2925 40.4903V37.479ZM15.2 43.7935C14.5924 43.4833 13.8483 43.7244 13.5381 44.332C13.2279 44.9396 13.469 45.6836 14.0766 45.9938C14.9198 46.4243 15.8744 46.6665 16.8819 46.6665H20.2171C20.8993 46.6665 21.4523 46.1135 21.4523 45.4313C21.4523 44.7491 20.8993 44.196 20.2171 44.196H16.8819C16.2739 44.196 15.7034 44.0505 15.2 43.7935ZM41.9216 45.9938C42.5292 45.6836 42.7703 44.9396 42.4601 44.332C42.1498 43.7244 41.4058 43.4833 40.7982 43.7935C40.2948 44.0505 39.7243 44.196 39.1163 44.196H35.7811C35.0989 44.196 34.5459 44.7491 34.5459 45.4313C34.5459 46.1135 35.0989 46.6665 35.7811 46.6665H39.1163C40.1238 46.6665 41.0784 46.4243 41.9216 45.9938ZM24.6639 44.196C23.9817 44.196 23.4287 44.7491 23.4287 45.4313C23.4287 46.1135 23.9817 46.6665 24.6639 46.6665H31.3342C32.0164 46.6665 32.5695 46.1135 32.5695 45.4313C32.5695 44.7491 32.0164 44.196 31.3342 44.196H24.6639ZM18.1183 7.13699C19.401 7.13699 20.5847 7.5376 21.6651 8.24791C22.2351 8.62268 23.0011 8.46438 23.3758 7.89434C23.7506 7.3243 23.5923 6.55837 23.0223 6.1836C21.5939 5.24452 19.9453 4.6665 18.1183 4.6665C16.9007 4.6665 15.8009 4.95342 14.8665 5.47068C14.2696 5.80109 14.0536 6.55278 14.384 7.14963C14.7144 7.74649 15.4661 7.96248 16.063 7.63208C16.6103 7.32908 17.2948 7.13699 18.1183 7.13699ZM29.2348 23.4019C29.2348 22.7197 28.6818 22.1667 27.9996 22.1667C27.3174 22.1667 26.7643 22.7197 26.7643 23.4019V29.3738H21C20.3178 29.3738 19.7647 29.9269 19.7647 30.6091C19.7647 31.2913 20.3178 31.8443 21 31.8443H26.7643V37.3333C26.7643 38.0155 27.3174 38.5686 27.9996 38.5686C28.6818 38.5686 29.2348 38.0155 29.2348 37.3333V31.8443H35C35.6822 31.8443 36.2352 31.2913 36.2352 30.6091C36.2352 29.9269 35.6822 29.3738 35 29.3738H29.2348V23.4019Z"
              fill="white"
            />
          </svg>
        </div>
        <div className="flex flex-col gap-[4px] text-center mt-4">
          <span className="text-primary font-semibold">
            {selectedApprovalFlow?.id
              ? "Update Approval Flow"
              : "Add a new Approval Flow"}
          </span>
          <span className="text-secondary text-sm">
            {selectedApprovalFlow?.id
              ? "Update this approval flow"
              : "Setup and publish a new approval flow"}
          </span>
        </div>
      </div>

      {/* Right section */}
      <div className="flex-1 flex flex-col p-3 md:pt-4 md:pr-6 md:pb-6 md:pl-10 w-full">
        {/* Close button */}
        <div className="flex justify-end">
          <svg
            className="cursor-pointer hover:scale-110 transition"
            onClick={() => setPopup(false)}
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.4099 12.0002L17.7099 7.71019C17.8982 7.52188 18.004 7.26649 18.004 7.00019C18.004 6.73388 17.8982 6.47849 17.7099 6.29019C17.5216 6.10188 17.2662 5.99609 16.9999 5.99609C16.7336 5.99609 16.4782 6.10188 16.2899 6.29019L11.9999 10.5902L7.70994 6.29019C7.52164 6.10188 7.26624 5.99609 6.99994 5.99609C6.73364 5.99609 6.47824 6.10188 6.28994 6.29019C6.10164 6.47849 5.99585 6.73388 5.99585 7.00019C5.99585 7.26649 6.10164 7.52188 6.28994 7.71019L10.5899 12.0002L6.28994 16.2902C6.19621 16.3831 6.12182 16.4937 6.07105 16.6156C6.02028 16.7375 5.99414 16.8682 5.99414 17.0002C5.99414 17.1322 6.02028 17.2629 6.07105 17.3848C6.12182 17.5066 6.19621 17.6172 6.28994 17.7102C6.3829 17.8039 6.4935 17.8783 6.61536 17.9291C6.73722 17.9798 6.86793 18.006 6.99994 18.006C7.13195 18.006 7.26266 17.9798 7.38452 17.9291C7.50638 17.8783 7.61698 17.8039 7.70994 17.7102L11.9999 13.4102L16.2899 17.7102C16.3829 17.8039 16.4935 17.8783 16.6154 17.9291C16.7372 17.9798 16.8679 18.006 16.9999 18.006C17.132 18.006 17.2627 17.9798 17.3845 17.9291C17.5064 17.8783 17.617 17.8039 17.7099 17.7102C17.8037 17.6172 17.8781 17.5066 17.9288 17.3848C17.9796 17.2629 18.0057 17.1322 18.0057 17.0002C18.0057 16.8682 17.9796 16.7375 17.9288 16.6156C17.8781 16.4937 17.8037 16.3831 17.7099 16.2902L13.4099 12.0002Z"
              fill="white"
            />
          </svg>
        </div>

        {/* Form fields */}
        <div className="flex flex-col gap-3 md:gap-6 flex-1">
          {/* Flow Name */}
          <div className="flex flex-col">
            <label className="flex justify-between text-primary font-semibold mb-2">
              Approval Flow name
              {!formValidFlowName && (
                <img src={errorIcon} alt="Error" className="w-5 h-5" />
              )}
            </label>
            <TextField
              placeholder="Enter approval flow name"
              value={flowName}
              onChange={(e) => {
                setFlowName(e.target.value);
                if (formSubmitted) checkForm(); // Revalidate on change
              }}
              className={!formValidFlowName ? "border-red-500" : ""}
            />
          </div>

          {/* Category */}
          <div className="flex flex-col">
            <label className="flex justify-between text-primary font-semibold mb-2">
              Category of spend
              {!formValidCategory && (
                <img src={errorIcon} alt="Error" className="w-5 h-5" />
              )}
            </label>

            <Listbox
              value={category}
              onChange={(value) => {
                setCategory(value);
                setFormValidCategory(true);
                // if (formSubmitted ) checkForm(); // Revalidate on change
              }}
            >
              <div className="relative mt-1">
                {/* Button */}
                <Listbox.Button
                  className={`relative bg-[#363D50] border ${
                    !formValidCategory ? "border-red-500" : "border-[#596073]"
                  } rounded-[12px] w-full px-[16px] py-[12px] text-left cursor-pointer`}
                >
                  <span>
                    {categoriesData?.categories?.find((c) => c?.id === category)?.name || "Select category of spend"}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronDownIcon className="h-5 w-5 text-secondary" />
                  </span>
                </Listbox.Button>

                {/* Options */}
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute mt-1 max-h-40 scrollbar-hide w-full overflow-auto bg-[#293043] p-2 rounded-[16px] z-10">
                    {Array.isArray(categoriesData?.categories) &&
                    categoriesData?.categories.length > 0
                      ? categoriesData?.categories?.map((cat) => (
                          <Listbox.Option
                            key={cat?.id}
                            value={cat?.id}
                            className={({ active, selected }) =>
                              `relative cursor-pointer py-2 pl-10 pr-4 rounded-[12px] border border-solid border-transparent mb-2 last:mb-0 
                              ${active ? "hover:bg-[#363D50]" : ""} 
                              ${selected ? "bg-[#363D50] !border-[#FFD74A]" : ""}`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span
                                  className={`flex ${selected ? "font-medium" : "font-normal"}`}
                                >
                                  {cat?.name}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-yellow-400">
                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))
                      : null}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>

          {/* Approvers */}
          <div className="flex flex-col">
            <label className="flex justify-between text-primary font-semibold mb-2">
              Add Approvers
              {!formValidApprovers && (
                <img src={errorIcon} alt="Error" className="w-5 h-5" />
              )}
            </label>

            <div className={`border rounded-lg bg-[#363D50] overflow-y-auto scrollbar-hide md:overflow-hidden ${formSubmitted && !formValidApprovers ? "border-red-500" : "border-[#596073]"}`}>
              {/* Header */}
              <div className="w-[600px] md:w-[auto] grid grid-cols-12 gap-2 bg-[#2C3245] text-[#A5AEBC] text-sm font-semibold px-2 md:px-4 py-2 sticky top-0">
                <div className="col-span-2 text-left px-2">Step</div>
                <div className="col-span-3 text-left px-2">Approver</div>
                <div className="col-span-3 text-left px-2">Note for Approver</div>
                <div className="col-span-3 text-left px-2">Min Spend</div>
                <div className="col-span-1"></div>
              </div>

              {/* Rows */}
              <div className="h-[160px] md:overflow-y-auto md:scrollbar-hide divide-y divide-[#596073]">
                {approvers.map((approver, index) => (
                  <div
                    key={index}
                    className="w-[600px] md:w-[auto] grid grid-cols-12 gap-2 px-2 py-2 md:py-3 items-center hover:bg-[#434A5D] transition"
                  >
                    {/* Step */}
                    <div className="col-span-2">
                      <TextField
                        type="number"
                        placeholder="Step"
                        value={approver.step}
                        onChange={(e) => updateApprover(index, "step", e.target.value)}
                      />
                    </div>

                    {/* Approver */}
                    <div className="col-span-3">
                      <SearchSelectDropdown
                        value={approver.approver}
                        onChange={(value) => updateApprover(index, "approver", value)}
                        users={CategoriesAndUsersData?.company_users || []}
                      />
                    </div>

                    {/* Note */}
                    <div className="col-span-3">
                      <TextField
                        placeholder="Note"
                        value={approver.note}
                        onChange={(e) => updateApprover(index, "note", e.target.value)}
                      />
                    </div>

                    {/* Min Spend */}
                    <div className="col-span-3">
                      <TextField
                        type="number"
                        placeholder="Min Spend"
                        value={approver.minSpend}
                        onChange={(e) => updateApprover(index, "minSpend", e.target.value)}
                      />
                    </div>

                    {/* Remove Approver */}
                    <div className="col-span-1 flex justify-center items-center">
                      <Button
                        type="button"
                        onClick={() => removeApprover(index)}
                        // disabled={approvers.length === 1}
                        fill
                        className={`!text-xl btnHover !rounded-[12px] !border-[#596073] !py-[9px] !px-[18px]
                          hover:text-[#E53E3E]}
                        `}
                      >
                        &times;
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Approver */}
              <div className="px-4 py-2 z-10">
                <Button
                  onClick={addApprover}
                  fill
                  className="btnHover"
                  disabled={approvers.some(
                    (a) =>
                      !a.approver ||
                      !a.step.trim() ||
                      // !a.note.trim() ||
                      !a.minSpend.trim()
                  ) }
                >
                  + Add Approver
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Checkbox: Allow requester to see approver comments */}
        <div className="flex items-center gap-2 md:gap-4 pt-2 md:pt-[15px]">
          <Checkbox
            id="commentVisible"
            name="commentVisible"
            checked={commentVisible}
            onClick={() => setCommentVisible((prev) => !prev)}
            className="border border-[#A5AEBC] rounded-[4px] cursor-pointer"
          />

          <label
            htmlFor="commentVisible"
            className="text-primary !font-[500] !text-[12px] md:!text-[14px] leading-none cursor-pointer"
          >
            Allow requester to see approver comments
          </label>
        </div>


        {/* Footer */}
        <div className="mt-2 md:mt-6 pt-2 flex justify-between items-center flex-col md:flex-row gap-3 md:gap-4 bg-[#363D50] rounded-b-[24px]">
          <div>
            {selectedApprovalFlow?.id && (
                <Button
                  onClick={handleDeleteApprovalFlow}
                  className="btnHover"
                  fill={true}
                  position="center"
                >
                  Delete Flow
                </Button>
              )}
          </div>
          <div className="flex items-center gap-[12px] md:pr-3">
            {validationMessage && (
              <div className="text-[#ED4650] mr-auto">{validationMessage}</div>
            )}
            <Button
              onClick={() => setPopup(false)}
              className="btnHover bg-gray-700"
              outlined
            >
              Cancel
            </Button>
            <Button onClick={submitApprovalFlowForm} className="btnHover" fill>
              {selectedApprovalFlow?.id ? "Update Flow" : "Add Flow"}
            </Button>
          </div>
        </div>
      </div>
      {confirmPopup && (
        <ConfirmModel
          setPopup={setConfirmPopup}
          id={clickedButton === "deleteFlow" ? selectedApprovalFlow?.id : removeStepId}
          setConfirmDelete={setConfirmDelete}
        />
      )}
    </div>
  );
}